import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";

const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "process", label: t.nav.process },
    { id: "results", label: t.nav.results },
    { id: "faq", label: t.nav.faq },
  ];

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header border-b border-slate-200/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => handleNav("top")}
          className="flex items-center gap-2 group"
        >
          <span className="relative w-9 h-9 rounded-md bg-slate-900 text-white grid place-items-center overflow-hidden">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 drop-shadow-[0_0_6px_rgba(56,189,248,0.9)]"
              aria-hidden="true"
            >
              <path d="M12 3c-3.5 0-6 2-6 5 0 2 .5 3.5 1 5.5s.5 4 1 6c.3 1.2 1 1.5 1.5 1.5s1-.3 1.3-1.5c.3-1.5.5-3 1.2-3s.9 1.5 1.2 3c.3 1.2.8 1.5 1.3 1.5s1.2-.3 1.5-1.5c.5-2 .5-4 1-6s1-3.5 1-5.5c0-3-2.5-5-6-5z" />
              <path d="M9 7c1-.6 2-.9 3-.9" opacity="0.6" />
            </svg>
            <span className="absolute top-1 right-1 w-1 h-1 rounded-full bg-sky-300 animate-pulse" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight">
            Chair<span className="text-sky-600">Flow</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <div
            data-testid="lang-switcher"
            className="flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white/80"
          >
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full transition ${
                lang === "es" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              ES
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full transition ${
                lang === "en" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              EN
            </button>
          </div>
          <Button
            data-testid="nav-cta-book"
            onClick={() => handleNav("contact")}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2 h-10 text-sm font-medium"
          >
            {t.nav.bookShort}
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left text-base font-medium text-slate-800 py-2"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 border border-slate-200 rounded-full p-1">
                <button
                  data-testid="mobile-lang-es"
                  onClick={() => setLang("es")}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    lang === "es" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  ES
                </button>
                <button
                  data-testid="mobile-lang-en"
                  onClick={() => setLang("en")}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    lang === "en" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  EN
                </button>
              </div>
              <Button
                data-testid="mobile-cta-book"
                onClick={() => handleNav("contact")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 h-10 text-sm"
              >
                {t.nav.bookDemo}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
